/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useToggle from 'react-use/lib/useToggle';
import { NavLink as Link } from 'react-router-dom';

import { Tooltip } from '@oca/ui';
import { createTransition } from '@oca/ui/theme';
import { getFirstLetters } from '@lib/help-fns';
import { ArrowDropDown } from '@oca/icons';
import { AccessControl } from './access-control';

const activeClassName = 'nav-link-active';
const topLevelActiveClassName = `${activeClassName}--top-level`;

export const NavList = React.memo(function NavList({
  depth,
  collapsed,
  location,
  onItemClick,
  pages = [],
}) {
  return (
    <List depth={depth} collapsed={collapsed}>
      {pages.map(page => {
        const hasSubPages = page.routes && page.routes.length > 0;

        if (!page.navigation) {
          return null;
        }

        const { icon, name, shortName, onlyTopLevelLink } =
          page.navigation || {};
        const shortLabel =
          shortName ||
          getFirstLetters(name || '')
            .slice(0, 2)
            .join('');

        if (!hasSubPages) {
          // Handle the only descendant. Move it to the top level.
          // eslint-disable-next-line no-param-reassign
          page =
            page.routes && page.routes.length === 1 ? page.routes[0] : page;
        }

        const isChildPageActive =
          location.pathname.startsWith(page.path) && depth === 0;

        return (
          <AccessControl
            key={page.path}
            allowed={page.access || []}
            accessCheck={page.accessCheck}
          >
            {hasSubPages && !onlyTopLevelLink ? (
              <NavItem
                className={isChildPageActive && activeClassName}
                icon={icon}
                collapsed={collapsed}
                label={name}
                shortLabel={shortLabel}
                initialOpen={isChildPageActive}
                target={page.target}
              >
                <NavList
                  depth={depth + 1}
                  pages={page.routes}
                  collapsed={collapsed}
                  location={location}
                  onItemClick={onItemClick}
                />
              </NavItem>
            ) : (
              <NavItem
                icon={icon}
                collapsed={collapsed}
                href={page.path}
                search={page.search}
                label={name}
                shortLabel={shortLabel}
                // Let active child routes highlight top level link
                exact={!onlyTopLevelLink}
                target={page.target}
                onClick={onItemClick}
              />
            )}
          </AccessControl>
        );
      })}
    </List>
  );
});

NavList.propTypes = {
  depth: PropTypes.number.isRequired,
  collapsed: PropTypes.bool.isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
  pages: PropTypes.arrayOf(PropTypes.object).isRequired,
  onItemClick: PropTypes.func.isRequired,
};

function NavItem({
  children,
  collapsed,
  exact,
  href,
  search,
  icon,
  initialOpen,
  label,
  onClick,
  shortLabel,
  target,
}) {
  const [on, toggle] = useToggle(initialOpen);

  const handleItemClick = event => {
    if (!href) {
      event.preventDefault();
      toggle(!on);
    } else {
      onClick();
    }
  };

  return (
    <ListItem>
      <Tooltip title={label} tooltipShown={collapsed ? undefined : false}>
        <NavLink
          className={initialOpen ? topLevelActiveClassName : undefined}
          to={{ pathname: href || '#', search }}
          onClick={handleItemClick}
          exact={exact}
          target={target}
        >
          <Adornment>{icon || shortLabel}</Adornment>
          <TextContainer hidden={collapsed}>{label}</TextContainer>
          {children && !collapsed && (
            <ExpandIcon fontSize={20} $expanded={on} />
          )}
        </NavLink>
      </Tooltip>
      {on && children}
    </ListItem>
  );
}

NavItem.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  exact: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  href: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  icon: PropTypes.node,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  // eslint-disable-next-line react/require-default-props
  onClick: PropTypes.func,
  shortLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  // eslint-disable-next-line react/require-default-props
  initialOpen: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  children: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  search: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  target: PropTypes.string,
};

NavItem.defaultProps = {
  exact: true,
};

const List = styled.ul`
  padding: 0;
  margin: 0 0 0 ${getListOffset}px;
`;

const ListItem = styled.li`
  display: block;
  list-style: none;
  & > div {
    display: block;
  }
`;

const NavLink = styled(Link).attrs({
  activeClassName,
})`
  position: relative;
  display: flex;
  align-items: center;
  height: 44px;
  /* 18px side margin matches the sidebar header/footer edges; with a 12px
     padding and a 20px icon the collapsed 80px rail gets a centred 44px tile. */
  margin: 4px 18px;
  padding: 0 12px;
  font-weight: 500;
  background-color: transparent;
  border-radius: 10px;
  transition: ${createTransition(['background-color', 'color'])};
  text-decoration: none;
  color: #64748b;
  outline: none;
  :hover {
    background-color: #f1f5f9;
    color: #334155;
  }
  :focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary.main};
  }
  &.${topLevelActiveClassName}, &.${activeClassName} {
    background-color: #7c44f41f;
    color: ${({ theme }) => theme.colors.primary.main};
    font-weight: 600;
  }
`;

const Adornment = styled.div`
  color: inherit;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 500;

  svg {
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.colors.textSecondary};
    transition: fill 0.3s;
  }

  /* when parent NavLink is active, override fill */
  ${NavLink}.${activeClassName} & svg {
    fill: ${({ theme }) => theme.colors.primary.main} !important;
  }

  ${NavLink}:hover & svg {
    fill: ${({ theme }) => theme.colors.primary.main} !important;
  }
`;

const TextContainer = styled.div`
  color: inherit;
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  font-size: 14px;
  transition: transform 300ms ease 0s, opacity 300ms ease 0s;
  white-space: nowrap;
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  transform: translate3d(${({ hidden }) => (hidden ? -25 : 0)}px, 0, 0);
`;

const ExpandIcon = styled(ArrowDropDown)`
  flex-shrink: 0;
  margin-left: 4px;
  transition: ${createTransition('transform')};
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
`;

function getListOffset({ collapsed, depth }) {
  if (collapsed) {
    return 0;
  }
  const nested = depth > 0;
  const extraOffset = nested ? 6 : 0;

  return depth * 8 + extraOffset;
}
